import {
  createContext,
  useContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryObserverResult,
} from "@tanstack/react-query";
import { useUserContext } from "@/app/_hooks/useUserContext";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  Timestamp,
  onSnapshot,
} from "firebase/firestore";
import { db } from "@/app/_firebase/firebase";
import { UserNotification } from "@/app/@types/user";

export interface NotificationsState {
  all: UserNotification[];
}

interface NotificationsContextType {
  notifications: UserNotification[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<QueryObserverResult<UserNotification[], Error>>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  createNotification: (
    notification: Omit<UserNotification, "read" | "readAt" | "createdAt">
  ) => Promise<void>;
}

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider = ({
  children,
}: NotificationsProviderProps) => {
  const user = useUserContext();
  const queryClient = useQueryClient();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Real-time subscription to notifications
  useEffect(() => {
    if (!user?.uid) return;

    setIsLoading(true);

    const unsubscribe = onSnapshot(
      doc(db, "users", user.uid, "metadata", "notifications"),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data() as NotificationsState;
          const newNotifications = (data.all || []).sort((a, b) => {
            // Sort by createdAt descending (newest first)
            const aTime = a.createdAt?.toMillis?.() || 0;
            const bTime = b.createdAt?.toMillis?.() || 0;
            return bTime - aTime;
          });

          // Only update if notifications have actually changed
          setNotifications((prevNotifications) => {
            // Check if arrays are deeply equal
            const hasChanged =
              prevNotifications.length !== newNotifications.length ||
              prevNotifications.some((prev, index) => {
                const curr = newNotifications[index];
                return (
                  prev.id !== curr.id ||
                  prev.read !== curr.read ||
                  prev.readAt !== curr.readAt
                );
              });

            if (hasChanged) {
              // Also update React Query cache for consistency
              queryClient.setQueryData(
                [user.uid, "notifications"],
                newNotifications
              );
              return newNotifications;
            }
            return prevNotifications;
          });
        } else {
          setNotifications([]);
          queryClient.setQueryData([user.uid, "notifications"], []);
        }
        setIsLoading(false);
        setError(null);
      },
      (err) => {
        setError(err as Error);
        setIsLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [user?.uid, queryClient]);

  // Keep the refetch function for manual refresh if needed
  const refetch = useCallback(async () => {
    const notificationsDoc = await getDoc(
      doc(db, "users", user.uid, "metadata", "notifications")
    );
    if (notificationsDoc.exists()) {
      const data = (notificationsDoc.data() as NotificationsState).all;
      setNotifications(data);
      return { data } as QueryObserverResult<UserNotification[], Error>;
    }
    return { data: [] } as QueryObserverResult<UserNotification[], Error>;
  }, [user.uid]);

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const notificationsRef = doc(
        db,
        "users",
        user.uid,
        "metadata",
        "notifications"
      );
      const notificationsDoc = await getDoc(notificationsRef);

      if (!notificationsDoc.exists()) {
        throw new Error("Notifications document not found");
      }

      const notifications = (notificationsDoc.data() as NotificationsState).all;
      const notification = notifications.find((n) => n.id === notificationId);

      if (!notification) {
        throw new Error("Notification not found");
      }

      notification.read = true;
      notification.readAt = Timestamp.now();

      await updateDoc(notificationsRef, {
        all: notifications,
      });
    },
    onMutate: async (notificationId) => {
      await queryClient.cancelQueries({
        queryKey: [user.uid, "notifications"],
      });
      const previousNotifications = queryClient.getQueryData([
        user.uid,
        "notifications",
      ]);

      queryClient.setQueryData(
        [user.uid, "notifications"],
        (old: UserNotification[] = []) =>
          old.map((n) =>
            n.id === notificationId
              ? { ...n, read: true, readAt: Timestamp.now() }
              : n
          )
      );

      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          [user.uid, "notifications"],
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [user.uid, "notifications"] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const notificationsRef = doc(
        db,
        "users",
        user.uid,
        "metadata",
        "notifications"
      );
      const notificationsDoc = await getDoc(notificationsRef);

      if (!notificationsDoc.exists()) {
        throw new Error("Notifications document not found");
      }

      const notifications = (notificationsDoc.data() as NotificationsState).all;

      const updatedNotifications = notifications.map((n) => {
        if (!n.read) {
          return {
            ...n,
            read: true,
            readAt: Timestamp.now(),
          };
        }
        return n;
      });

      await updateDoc(notificationsRef, {
        all: updatedNotifications,
      });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: [user.uid, "notifications"],
      });
      const previousNotifications = queryClient.getQueryData([
        user.uid,
        "notifications",
      ]);

      const optimistic = (previousNotifications as UserNotification[] | undefined)?.map(
        (n) => (n.read ? n : { ...n, read: true, readAt: Timestamp.now() })
      );

      if (optimistic) {
        setNotifications(optimistic);
      }

      queryClient.setQueryData(
        [user.uid, "notifications"],
        (old: UserNotification[] = []) =>
          old.map((n) =>
            n.read ? n : { ...n, read: true, readAt: Timestamp.now() }
          )
      );

      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          [user.uid, "notifications"],
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [user.uid, "notifications"] });
    },
  });

  const markAllAsRead = useCallback(async () => {
    await markAllAsReadMutation.mutateAsync();
  }, [markAllAsReadMutation]);

  const createNotificationMutation = useMutation({
    mutationFn: async (
      notification: Omit<
        UserNotification,
        "read" | "readAt" | "createdAt"
      > & { isAdmin?: boolean }
    ) => {
      const notificationsRef = doc(
        db,
        "users",
        user.uid,
        "metadata",
        "notifications"
      );

      const newNotification: UserNotification = {
        ...notification,
        isAdmin: notification.isAdmin ?? false,
        read: false,
        readAt: null,
        createdAt: Timestamp.now(),
      };

      await updateDoc(notificationsRef, {
        all: arrayUnion(newNotification),
      });
    },
    onMutate: async (
      notification: Omit<
        UserNotification,
        "read" | "readAt" | "createdAt"
      > & { isAdmin?: boolean }
    ) => {
      await queryClient.cancelQueries({
        queryKey: [user.uid, "notifications"],
      });
      const previousNotifications = queryClient.getQueryData([
        user.uid,
        "notifications",
      ]);

      const newNotification: UserNotification = {
        ...notification,
        isAdmin: notification.isAdmin ?? false,
        read: false,
        readAt: null,
        createdAt: Timestamp.now(),
      };

      queryClient.setQueryData(
        [user.uid, "notifications"],
        (old: UserNotification[] = []) => [...old, newNotification]
      );

      return { previousNotifications };
    },
    onError: (err, variables, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(
          [user.uid, "notifications"],
          context.previousNotifications
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [user.uid, "notifications"] });
    },
  });

  const markAsRead = useCallback(
    async (notificationId: string) => {
      await markAsReadMutation.mutateAsync(notificationId);
    },
    [markAsReadMutation]
  );

  const createNotification = useCallback(
    async (
      notification: Omit<UserNotification, "read" | "readAt" | "createdAt">
    ) => {
      await createNotificationMutation.mutateAsync(notification);
    },
    [createNotificationMutation]
  );

  const value = useMemo(
    () => ({
      notifications,
      isLoading,
      error,
      markAsRead,
      markAllAsRead,
      createNotification,
      refetch,
    }),
    [
      notifications,
      isLoading,
      error,
      markAsRead,
      markAllAsRead,
      createNotification,
      refetch,
    ]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error(
      "useNotificationsContext must be used within a NotificationsProvider"
    );
  }
  return context;
};
